/**
 * PrepressConfigOverlay — per-carton prepress config (PR ⑭e, rev 5 §4).
 *
 * WHY AN OVERLAY, AND WHY PER CARTON
 * ----------------------------------
 * Rev 5 §4 is the last ⚠ REDESIGN item and its point is that config is a
 * property of the CARTON, not of the job. The trigger therefore lives on the
 * bench carton row — the same structural move ⑬b made for the mode — so there
 * is never a control whose scope is ambiguous.
 *
 * The editor itself is an overlay rather than an expanded rail row because
 * `describe` returns four flap regions, a resolved config, panel widths and a
 * preview. In a mixed job that content pushes the carton list off screen, and
 * the rail is where an operator compares cartons against each other.
 *
 * WHY IT CANNOT LIVE ON THE REPORT CARD
 * -------------------------------------
 * The report card reads a RESPONSE. Config must be set BEFORE the run. That is
 * the hole rev 6 §3 opened `describe` to fill.
 *
 * THE BINDING RULE (rev 6 §4, rev 7 §5)
 * -------------------------------------
 * Sliders bind to `search_mm`, never `range_mm`. `range_mm` is the LEGAL range;
 * `search_mm` is what the engine will actually search. On the real Ginkoline
 * glue flap those are [12,17] and [12,12] — a slider drawn from the legal range
 * offers an adjustment the engine refuses every time. `dust` and `bf` are
 * derived formulas with no adjustable range at all.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from './theme';
import DiePreview from './DiePreview';
import {
  describeRequest, parseDescribe, flapSlidersBindable, flapSliderBlockReason,
  flapRowEditable, describePrepressError, previewViewBox, previewIsRenderable,
  bindFlapRows, DEFAULT_CARTON_TYPE,
} from './prepress';
import { describePrepressCarton } from './exportVector';

/** Backend recommended 150–250 ms; the editor is the only interactive route. */
const DESCRIBE_DEBOUNCE_MS = 200;


export default function PrepressConfigOverlay({
  carton, apiBase, caps, offeredTypes, onApply, onClose,
}) {
  const { P, FN, MO } = useTheme();
  const [config, setConfig] = useState(() => ({ ...(carton?.prepressConfig || {}) }));
  /**
   * ⑭i: the carton described with NO config of ours. This is what the sliders
   * are bound to, for the whole life of the editor. See `bindFlapRows` for why
   * — in one line: a span narrowed by our own request is an echo, not a
   * measurement, and binding a control to its own echo makes a ratchet.
   */
  const [baseline, setBaseline] = useState(null);
  const [described, setDescribed] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const baseAbortRef = useRef(null);
  // ⑭k / rev 12 §5: both gates are per FAMILY. `shapes.flap_regions` is the BL
  // answer and now says so with `applies_to`, so reading it for a tuck carton
  // is how a `tf` slider would appear on a carton that has no `tf`. A carton
  // with no explicit type is a BL on the wire — see DEFAULT_CARTON_TYPE.
  const family = carton?.cartonType || DEFAULT_CARTON_TYPE;
  const bindable = flapSlidersBindable(caps, family);
  const blockReason = flapSliderBlockReason(caps, family);

  /**
   * ⑭i — deps are VALUES, not object identities.
   *
   * `carton` is `vecFiles.find(...)` and `offeredTypes` is
   * `prepressCaps?.cartonTypes || []`. Both change identity on ordinary parent
   * re-renders — editing a carton's count, or any render at all before
   * capabilities has answered, because `|| []` is a fresh array each time. The
   * effect below depends on `runDescribe`, so each of those cost a describe
   * with a config that had not changed. Measured: +1 request per parent render.
   */
  const vertices = carton?.detection?.vertices || null;
  const verticesKey = useMemo(() => JSON.stringify(vertices), [vertices]);
  const cartonType = carton?.cartonType || null;
  const offeredKey = useMemo(
    () => (Array.isArray(offeredTypes) ? offeredTypes.join('|') : ''),
    [offeredTypes],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableVertices = useMemo(() => vertices, [verticesKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOffered = useMemo(() => offeredTypes, [offeredKey]);

  /**
   * One in-flight describe at a time, previous aborted.
   *
   * Without the abort an out-of-order response repaints the editor with ranges
   * for a slider position the operator has already left — numbers that look
   * settled and are not. Backend enforces no rate limit, shares the process
   * with nesting jobs, and pays a package load on the first call, so the
   * debounce is ours to get right.
   */
  const readError = useCallback((e) => {
    // Same structured shape as a run, so one error reader serves both.
    const d = describePrepressError(e?.body);
    return d ? `${d.title}${d.hint ? ` — ${d.hint}` : ''}` : (e?.message || 'Could not read this carton.');
  }, []);

  const runDescribe = useCallback((cfg) => {
    const body = describeRequest({
      vertices: stableVertices, config: cfg, cartonType, offeredTypes: stableOffered,
    });
    if (!body) { setError('This carton has no detected outline to describe.'); return; }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBusy(true);
    describePrepressCarton({ apiBase, body, signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setDescribed(parseDescribe(res));
        setError(null);
      })
      .catch((e) => {
        if (ctrl.signal.aborted || e?.name === 'AbortError') return;
        setError(readError(e));
        setDescribed(null);
      })
      .finally(() => { if (!ctrl.signal.aborted) setBusy(false); });
  }, [apiBase, stableVertices, cartonType, stableOffered, readError]);

  /**
   * The baseline: ONE describe with no config, per carton, per editor session.
   *
   * `plain` needs no sheet and is sub-second (rev 6 §3), which is the whole
   * reason `describe` exists as a pre-run route — so this costs one cheap call
   * on open and buys a control whose extent does not move.
   */
  useEffect(() => {
    const body = describeRequest({
      vertices: stableVertices, config: null, cartonType, offeredTypes: stableOffered,
    });
    if (!body) return undefined;
    const ctrl = new AbortController();
    baseAbortRef.current = ctrl;
    describePrepressCarton({ apiBase, body, signal: ctrl.signal })
      .then((res) => { if (!ctrl.signal.aborted) setBaseline(parseDescribe(res)); })
      .catch((e) => {
        if (ctrl.signal.aborted || e?.name === 'AbortError') return;
        setError(readError(e));
      });
    return () => ctrl.abort();
  }, [apiBase, stableVertices, cartonType, stableOffered, readError]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Nothing set yet → the baseline call above already describes exactly this,
    // so a second identical request would be pure duplication.
    if (Object.keys(config).length === 0) return undefined;
    timerRef.current = setTimeout(() => runDescribe(config), DESCRIBE_DEBOUNCE_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [config, runDescribe]);

  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
    if (baseAbortRef.current) baseAbortRef.current.abort();
  }, []);

  /**
   * ⑭h: the key comes from the ROW, which resolved it and confirmed the server
   * knows it. The editor no longer maps a flap name to a config key itself —
   * doing that is what produced `{"undefined": […]}` on the first slider drag,
   * because the package names flaps `glue`/`tuck` while the config keys are
   * `gf_`/`tf_`. A row with no confirmed key is not adjustable, so this cannot
   * be reached with a null one; the guard is here because "cannot be reached"
   * is the claim that was wrong last time.
   */
  const setSlab = (configKey, lo, hi) => {
    if (!configKey) return;
    setConfig((prev) => ({ ...prev, [configKey]: [lo, hi] }));
  };

  // ⑭i: bounds from the baseline, readouts from the live description.
  const rows = bindFlapRows(baseline?.entry, described?.entry);
  const preview = described?.preview || baseline?.preview || null;

  const card = {
    background: P.sheet, border: `0.5px solid ${P.rule}`, borderRadius: 10,
    padding: 18, width: 'min(680px, 94vw)', maxHeight: '88vh', overflowY: 'auto',
  };
  const eyebrow = {
    fontFamily: MO, fontSize: 10, fontWeight: 600, letterSpacing: 1,
    textTransform: 'uppercase', color: P.dim,
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={eyebrow}>Prepress config</div>
          <button type="button" onClick={onClose} style={{
            background: 'none', border: 'none', color: P.muted, cursor: 'pointer',
            fontFamily: MO, fontSize: 11,
          }}>close</button>
        </div>
        <div style={{ fontFamily: FN, fontSize: 15, color: P.ink, marginBottom: 12 }}>
          {carton?.name}
        </div>

        {/* The measurement gate, stated rather than implied. A slider that
            cannot move is confusing; a row that says why it cannot is not. */}
        {!bindable && (
          <div style={{
            fontFamily: FN, fontSize: 11, color: P.ink, lineHeight: 1.5,
            padding: '8px 10px', borderRadius: 6, background: P.warnSoft,
            border: `0.5px solid ${P.warn}`, marginBottom: 12,
          }}>{blockReason}</div>
        )}

        {error && (
          <div style={{
            fontFamily: FN, fontSize: 11, color: P.ink, lineHeight: 1.5,
            padding: '8px 10px', borderRadius: 6, background: P.dangerSoft,
            border: `0.5px solid ${P.dangerLine}`, marginBottom: 12,
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            {rows.length === 0 && !error && (
              <div style={{ fontFamily: MO, fontSize: 10.5, color: P.dim }}>
                {busy ? 'Reading this carton…' : 'No flap ranges reported for this carton.'}
              </div>
            )}

            {rows.map((row) => {
              const editable = flapRowEditable(row, caps, family);
              // ⑭i: the control's extent, from the BASELINE. Binding this to the
              // live response is what let each drag rewrite the slider's own
              // floor until a drag back down was silently clamped to a no-op.
              const span = row.bounds;
              const cfgVal = row.configKey ? config[row.configKey] : undefined;
              const lo = Array.isArray(cfgVal) ? cfgVal[0] : (span ? span[0] : null);
              const hi = Array.isArray(cfgVal) ? cfgVal[1] : (span ? span[1] : null);
              return (
                <div key={row.rowId} style={{
                  padding: '9px 0', borderBottom: `0.5px solid ${P.ruleSoft}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontFamily: MO, fontSize: 11, color: P.ink }}>{row.label}</span>
                    <span style={{ fontFamily: MO, fontSize: 11, color: P.muted }}>
                      {row.currentMm !== null ? `${row.currentMm} mm` : '—'}
                    </span>
                  </div>

                  {/* Derived flaps have no adjustable range at all — they are
                      formulas off the wall they hang from (rev 6 §4). */}
                  {row.derived ? (
                    <div style={{ fontFamily: MO, fontSize: 9.5, color: P.dim, marginTop: 3 }}>
                      derived — not adjustable
                    </div>
                  ) : !editable ? (
                    <div style={{ fontFamily: MO, fontSize: 9.5, color: P.dim, marginTop: 3 }}>
                      {row.locked
                        ? `locked${row.lockedReason ? ` — ${row.lockedReason}` : ''}`
                        : !row.configKey
                          ? 'this server did not report a setting for this flap'
                          : 'not adjustable on this server'}
                      {span && ` · searches ${span[0]}–${span[1]} mm`}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 5 }}>
                      <input
                        type="range"
                        min={span[0]} max={span[1]} step={0.5} value={lo ?? span[0]}
                        onChange={(e) => setSlab(row.configKey, parseFloat(e.target.value), hi ?? span[1])}
                        style={{ flex: 1 }}
                      />
                      <span style={{ fontFamily: MO, fontSize: 10, color: P.muted, minWidth: 96 }}>
                        {lo ?? span[0]}–{hi ?? span[1]} mm
                      </span>
                    </div>
                  )}
                  {/* The legal range is shown as context and never bound. */}
                  {row.legal && span && (row.legal[0] !== span[0] || row.legal[1] !== span[1]) && (
                    <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, marginTop: 3 }}>
                      legal {row.legal[0]}–{row.legal[1]} mm · the engine searches {span[0]}–{span[1]}
                    </div>
                  )}
                  {/* ⑭i: what the engine says it will search UNDER WHAT YOU SET.
                      This is the echo — useful as feedback, never as a bound. */}
                  {editable && row.resolved && span
                    && (row.resolved[0] !== span[0] || row.resolved[1] !== span[1]) && (
                    <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, marginTop: 3 }}>
                      with your setting the engine searches {row.resolved[0]}–{row.resolved[1]} mm
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* `describe` returns a preview in the same shape as
              prepress_previews[]. No counts, no selection — nothing was
              measured against a sheet, and a yield figure inside a config
              editor gets read as a prediction. */}
          {previewIsRenderable(preview) && (
            <div style={{ flex: '0 0 200px' }}>
              <div style={{ ...eyebrow, marginBottom: 6 }}>Blank</div>
              <DiePreview
                vertices={preview.after?.vertices_mm}
                viewBox={previewViewBox(preview)}
                size={200}
              />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button type="button" onClick={() => onApply(config)} style={{
            fontFamily: MO, fontSize: 11, padding: '6px 12px', borderRadius: 5,
            background: P.accent, color: P.sheet, border: 'none', cursor: 'pointer',
          }}>Apply to this carton</button>
          <button type="button" onClick={() => { setConfig({}); }} style={{
            fontFamily: MO, fontSize: 11, padding: '6px 12px', borderRadius: 5,
            background: 'transparent', color: P.muted, border: `0.5px solid ${P.ruleSoft}`, cursor: 'pointer',
          }}>Reset to defaults</button>
          {busy && <span style={{ fontFamily: MO, fontSize: 10, color: P.dim }}>reading…</span>}
        </div>
      </div>
    </div>
  );
}
